import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Stack,
  Typography,
  Card,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Chip,
  Box,
  CircularProgress,
  IconButton,
  Divider,
  Alert,
  MenuItem,
  InputAdornment,
  Grid,
} from '@mui/material';
import {
  ArrowLeft,
  Save,
  Server,
  Eye,
  EyeOff,
  History,
  Wallet,
  ShieldAlert,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Key,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Page from '../components/Page';
import {
  getServiceProviders,
  updateServiceProvider,
  getAvailableProcessors,
  validateServiceProviderCredentials,
} from '../api/services';
import { fCurrency } from '../utils/formatNumber';
import { hasPermission, PERMISSIONS, isAdminOrOwner } from '../utils/hasPermission';
import VendorSelectModal, { getDriverTheme } from './components/Services/VendorSelectModal';

function safeBtoa(str) {
  try {
    return btoa(unescape(encodeURIComponent(str)));
  } catch (e) {
    return btoa(str);
  }
}

function safeAtob(str) {
  try {
    return decodeURIComponent(escape(atob(str)));
  } catch (e) {
    try {
      return atob(str);
    } catch {
      return '';
    }
  }
}

export default function ServiceProviderEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);
  const activeApp = useSelector((state) => state.apps?.activeApp);
  const isPrivileged = isAdminOrOwner(user, activeApp);
  const canManage = isPrivileged && hasPermission(user, [
    PERMISSIONS.SERVICES_WRITE,
    PERMISSIONS.PROCESSORS_MANAGE,
  ]);

  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [processors, setProcessors] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedProcessor, setSelectedProcessor] = useState(null);
  const [openVendorModal, setOpenVendorModal] = useState(false);

  const [adexUsername, setAdexUsername] = useState('');
  const [adexPassword, setAdexPassword] = useState('');
  const [autopilotEmail, setAutopilotEmail] = useState('');

  // Key validation state
  const [testingKey, setTestingKey] = useState(false);
  const [keyValidation, setKeyValidation] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    apiKey: '',
    email: '',
    baseUrl: '',
    driverType: 'msorg',
    authHeaderType: 'Bearer',
    lowBalanceThreshold: 5000,
    isActive: true,
    supportsBalanceCheck: true,
    balanceMode: 'api_sync',
    balance: 0,
    addBalance: '',
    auditNotes: '',
  });

  const fetchData = useCallback(async () => {
    if (!isPrivileged) {
      setLoading(false);
      setLoadingTemplates(false);
      return;
    }
    setLoading(true);
    setLoadingTemplates(true);
    try {
      const [provRes, procRes] = await Promise.all([
        getServiceProviders(),
        getAvailableProcessors().catch(() => ({ ok: false })),
      ]);

      let procList = [];
      if (procRes?.ok && procRes.data?.data?.processors?.length > 0) {
        procList = [...procRes.data.data.processors];
        if (!procList.some((p) => p.name === 'custom')) {
          procList.push({
            name: 'custom',
            displayName: 'Custom Provider Gateway',
            driverType: 'custom',
            baseUrl: '',
            authHeaderType: 'Bearer',
            supportedCategories: ['data', 'airtime', 'cable', 'electricity', 'result_check'],
            capabilities: { canFetchBalance: false },
          });
        }
        setProcessors(procList);
      }

      if (provRes.ok && provRes.data?.data) {
        const found = provRes.data.data.find((p) => String(p.id) === String(id));
        if (found) {
          setProvider(found);

          if (procList.length > 0) {
            const matched = procList.find(
              (p) =>
                (found.providerId && p._id === found.providerId) ||
                p.name?.toLowerCase() === (found.slug || '').toLowerCase() ||
                p.name?.toLowerCase().replace(/[^a-z0-9]/g, '') === (found.slug || '').toLowerCase().replace(/[^a-z0-9]/g, '') ||
                (p.driverType?.toLowerCase() === (found.driverType || '').toLowerCase() &&
                  p.baseUrl?.toLowerCase() === (found.baseUrl || '').toLowerCase())
            );
            if (matched) {
              setSelectedProcessor(matched);
            }
          }

          const isAdex = (found.driverType || '').toLowerCase() === 'adex';
          let initialUser = '';
          let initialPass = '';
          if (isAdex && found.apiKey) {
            try {
              let raw = found.apiKey.trim();
              if (raw.startsWith('Basic ')) raw = raw.replace(/^Basic\s+/, '');
              const decoded = safeAtob(raw);
              if (decoded && decoded.includes(':')) {
                const idx = decoded.indexOf(':');
                initialUser = decoded.slice(0, idx);
                initialPass = decoded.slice(idx + 1);
              }
            } catch (decodeErr) {
              // Ignore decoding error
            }
          }
          setAdexUsername(initialUser);
          setAdexPassword(initialPass);

          const isAutopilot = (found.driverType || '').toLowerCase() === 'autopilot';
          let initialAutopilotEmail = found.email || '';
          let initialApiKey = found.apiKey || '';
          if (isAutopilot && found.apiKey && found.apiKey.includes(':')) {
            const parts = found.apiKey.split(':');
            if (parts[0].includes('@')) {
              if (!initialAutopilotEmail) initialAutopilotEmail = parts[0].trim();
              initialApiKey = parts.slice(1).join(':').trim();
            }
          }
          setAutopilotEmail(initialAutopilotEmail);

          const supportsCheck = found.supportsBalanceCheck ?? true;
          setFormData({
            name: found.name || '',
            apiKey: isAutopilot ? initialApiKey : (found.apiKey || ''),
            email: found.email || initialAutopilotEmail || '',
            baseUrl: found.baseUrl || '',
            driverType: found.driverType || 'msorg',
            authHeaderType: found.authHeaderType || (isAdex ? 'Token' : 'Bearer'),
            lowBalanceThreshold: found.lowBalanceThreshold != null ? found.lowBalanceThreshold : 5000,
            isActive: found.isActive ?? true,
            supportsBalanceCheck: supportsCheck,
            balanceMode: found.balanceMode || (supportsCheck ? 'api_sync' : 'manual_ledger'),
            balance: found.balance != null ? Number(found.balance) : 0,
            addBalance: '',
            auditNotes: '',
          });
        } else {
          toast.error(`Service Provider #${id} not found.`);
        }
      }
    } catch (err) {
      toast.error('Error fetching provider credentials.');
    } finally {
      setLoading(false);
      setLoadingTemplates(false);
    }
  }, [id, isPrivileged]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectProcessor = (proc) => {
    setSelectedProcessor(proc);
    setKeyValidation(null);
    if (proc) {
      const isTokenDriver =
        (proc.driverType || '').toLowerCase() === 'adex' ||
        (proc.driverType || '').toLowerCase() === 'msorg' ||
        (proc.name || '').toLowerCase() === 'datatap' ||
        (proc.name || '').toLowerCase() === 'quicklysim';

      setFormData((prev) => ({
        ...prev,
        baseUrl: proc.baseUrl || prev.baseUrl,
        driverType: isTokenDriver ? (proc.driverType || 'msorg') : proc.driverType || prev.driverType || 'custom',
        authHeaderType: isTokenDriver ? 'Token' : proc.authHeaderType || prev.authHeaderType || 'Bearer',
      }));
      toast.info(`Selected vendor template: ${proc.displayName || proc.name}`);
    }
  };

  const isAdexDriver =
    (formData.driverType || '').toLowerCase() === 'adex' ||
    (selectedProcessor?.driverType || '').toLowerCase() === 'adex' ||
    (selectedProcessor?.name || '').toLowerCase() === 'datatap' ||
    (selectedProcessor?.name || '').toLowerCase() === 'quicklysim';

  const isAutopilotDriver =
    (formData.driverType || '').toLowerCase() === 'autopilot' ||
    (selectedProcessor?.driverType || '').toLowerCase() === 'autopilot' ||
    (selectedProcessor?.name || '').toLowerCase() === 'autopilot';

  const handleTestKey = async () => {
    if (isAdexDriver) {
      if (!adexUsername.trim() || !adexPassword) {
        toast.error('Please enter your portal username and password first.');
        return;
      }
    } else if (!formData.apiKey.trim()) {
      toast.error('Please enter an API key to test.');
      return;
    }

    if (!formData.baseUrl.trim()) {
      toast.error('Base URL is missing for this provider.');
      return;
    }

    setTestingKey(true);
    try {
      const emailVal = (formData.email || autopilotEmail || '').trim();
      let resolvedApiKey = formData.apiKey.trim();
      if (isAdexDriver) {
        resolvedApiKey = safeBtoa(`${adexUsername.trim()}:${adexPassword}`);
      } else if (isAutopilotDriver && emailVal) {
        resolvedApiKey = resolvedApiKey.includes(':')
          ? resolvedApiKey
          : `${emailVal}:${resolvedApiKey}`;
      }

      let resolvedUsername;
      if (isAutopilotDriver && emailVal) {
        resolvedUsername = emailVal;
      } else if (isAdexDriver) {
        resolvedUsername = adexUsername.trim();
      }

      const payload = {
        name: formData.name.trim() || provider?.name || 'Verification',
        driverType: isAdexDriver ? 'adex' : formData.driverType || provider?.driverType || 'msorg',
        baseUrl: formData.baseUrl.trim(),
        apiKey: resolvedApiKey,
        email: emailVal || undefined,
        username: resolvedUsername,
        authHeaderType: isAdexDriver ? 'Token' : formData.authHeaderType || 'Bearer',
      };

      const res = await validateServiceProviderCredentials(payload);
      if (res.ok && res.data?.data?.valid) {
        const { data } = res.data;
        setKeyValidation({
          status: 'success',
          message: data.message || 'Credentials verified successfully.',
          balance: data.balance ?? 0,
          username: data.username || (isAdexDriver ? adexUsername.trim() : null),
        });
        if (data.tokenType) {
          setFormData((prev) => ({ ...prev, authHeaderType: data.tokenType }));
        }
        toast.success(`Credentials verified! Live balance: ₦${fCurrency(data.balance ?? 0)}`);
      } else {
        const errMsg = res.data?.message || 'Failed to verify credentials with vendor.';
        setKeyValidation({
          status: 'error',
          message: errMsg,
        });
        toast.error(errMsg);
      }
    } catch (err) {
      const errMsg =
        err.response?.data?.message ||
        err.message ||
        'Error connecting to vendor for verification.';
      setKeyValidation({
        status: 'error',
        message: errMsg,
      });
      toast.error(errMsg);
    } finally {
      setTestingKey(false);
    }
  };

  const renderVerificationStatus = () => {
    if (keyValidation?.status === 'success') {
      const usernameText = keyValidation.username ? `Connected as "${keyValidation.username}". ` : '';
      return (
        <Box
          sx={{
            p: 1.75,
            borderRadius: '10px',
            bgcolor: '#F0FDF4',
            border: '1px solid #BBF7D0',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '8px',
                  bgcolor: '#DCFCE7',
                  color: '#16A34A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: 0.25,
                }}
              >
                <CheckCircle2 size={16} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#166534' }}>
                  Credentials Verified
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#15803D', mt: 0.25 }}>
                  {usernameText}
                  Vendor confirmed account is valid and operational.
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.35,
                      borderRadius: '6px',
                      bgcolor: '#DCFCE7',
                      border: '1px solid #86EFAC',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.725rem', fontWeight: 600, color: '#166534' }}>
                      Live Wallet Balance:
                    </Typography>
                    <Typography sx={{ fontSize: '0.775rem', fontWeight: 800, color: '#14532D' }}>
                      ₦{fCurrency(keyValidation.balance ?? 0)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Stack>
            <Button
              size="small"
              variant="text"
              disabled={testingKey}
              onClick={handleTestKey}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#15803D',
                p: 0.5,
                minWidth: 'auto',
                '&:hover': { bgcolor: '#DCFCE7' },
              }}
            >
              Re-test
            </Button>
          </Stack>
        </Box>
      );
    }

    if (keyValidation?.status === 'error') {
      return (
        <Box
          sx={{
            p: 1.75,
            borderRadius: '10px',
            bgcolor: '#FEF2F2',
            border: '1px solid #FECACA',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '8px',
                  bgcolor: '#FEE2E2',
                  color: '#DC2626',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: 0.25,
                }}
              >
                <AlertCircle size={16} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#991B1B' }}>
                  Credential Verification Failed
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#B91C1C', mt: 0.25 }}>
                  {keyValidation.message}
                </Typography>
              </Box>
            </Stack>
            <Button
              size="small"
              variant="contained"
              disabled={testingKey}
              onClick={handleTestKey}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '8px',
                bgcolor: '#DC2626',
                color: '#FFFFFF',
                '&:hover': { bgcolor: '#B91C1C' },
                flexShrink: 0,
              }}
            >
              {testingKey ? <CircularProgress size={14} color="inherit" /> : 'Retry'}
            </Button>
          </Stack>
        </Box>
      );
    }

    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography sx={{ fontSize: '0.825rem', fontWeight: 600, color: '#374151' }}>
            Test Credentials
          </Typography>
          <Typography variant="caption" sx={{ color: '#6B7280' }}>
            Verify your key against the vendor to confirm live balance and credentials.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          disabled={testingKey || !canManage}
          onClick={handleTestKey}
          startIcon={testingKey ? <CircularProgress size={14} /> : <Key size={14} />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            borderRadius: '10px',
            borderColor: '#D1D5DB',
            color: '#374151',
            flexShrink: 0,
            px: 2,
            py: 0.75,
            '&:hover': { borderColor: '#0066FE', color: '#0066FE', bgcolor: '#EFF6FF' },
          }}
        >
          {testingKey ? 'Testing...' : 'Test Key'}
        </Button>
      </Stack>
    );
  };

  const handleSave = async () => {
    if (!provider) return;
    if (!isPrivileged || !canManage) {
      toast.error('Only administrators and owners can modify service providers.');
      return;
    }

    if (isAdexDriver) {
      if (!adexUsername.trim()) {
        toast.error('Please enter your portal username or email.');
        return;
      }
      if (!adexPassword) {
        toast.error('Please enter your portal password.');
        return;
      }
    } else if (!formData.apiKey.trim()) {
      toast.error('Please enter your vendor API key.');
      return;
    }

    setSaving(true);
    try {
      const emailVal = (formData.email || autopilotEmail || '').trim();
      let resolvedApiKey = formData.apiKey.trim();
      if (isAdexDriver) {
        resolvedApiKey = safeBtoa(`${adexUsername.trim()}:${adexPassword}`);
      } else if (isAutopilotDriver && emailVal) {
        resolvedApiKey = resolvedApiKey.includes(':')
          ? resolvedApiKey
          : `${emailVal}:${resolvedApiKey}`;
      }

      const payload = {
        name: formData.name.trim(),
        slug: selectedProcessor?.name || provider.slug,
        providerId: selectedProcessor?._id || provider.providerId || undefined,
        driverConfig: selectedProcessor
          ? {
              customRoutes: selectedProcessor.customRoutes || null,
              customConfig: selectedProcessor.customConfig || null,
              capabilities: selectedProcessor.capabilities || null,
            }
          : provider.driverConfig || undefined,
        driverType: isAdexDriver ? 'adex' : (formData.driverType || 'msorg'),
        authHeaderType: isAdexDriver ? 'Token' : (formData.authHeaderType || 'Bearer'),
        apiKey: resolvedApiKey,
        email: emailVal || undefined,
        baseUrl: formData.baseUrl.trim(),
        lowBalanceThreshold: Number(formData.lowBalanceThreshold) || 5000,
        isActive: formData.isActive,
        supportsBalanceCheck: formData.supportsBalanceCheck,
        balanceMode: formData.balanceMode,
        balance: Number(formData.balance) >= 0 ? Number(formData.balance) : 0,
        notes: formData.auditNotes?.trim() || undefined,
      };

      if (formData.addBalance && !Number.isNaN(Number(formData.addBalance)) && Number(formData.addBalance) > 0) {
        payload.addBalance = Number(formData.addBalance);
      }

      const res = await updateServiceProvider(provider.id, payload);
      if (res.ok) {
        toast.success(`Updated settings for ${formData.name} successfully!`);
        navigate(`/dashboard/service-providers/details/${provider.id}`);
      } else {
        toast.error(res.data?.message || 'Failed to update service provider.');
      }
    } catch (err) {
      toast.error('Error updating service provider.');
    } finally {
      setSaving(false);
    }
  };

  if (!isPrivileged) {
    return (
      <Page title="Access Denied | Service Providers">
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Card sx={{ p: 4, textAlign: 'center', borderRadius: '16px', border: '1px solid #FECACA', bgcolor: '#FEF2F2' }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <ShieldAlert size={24} />
            </Box>
            <Typography variant="h6" sx={{ color: '#991B1B', fontWeight: 700, mb: 1 }}>
              Restricted Security Settings
            </Typography>
            <Typography variant="body2" sx={{ color: '#7F1D1D', maxWidth: 480, mx: 'auto' }}>
              Service provider credentials and configurations are strictly restricted to Workspace Administrators and Owners.
            </Typography>
          </Card>
        </Container>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page title="Edit Service Provider | Dashboard">
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="center" py={12}>
            <CircularProgress />
          </Box>
        </Container>
      </Page>
    );
  }

  if (!provider) {
    return (
      <Page title="Edit Service Provider | Dashboard">
        <Container maxWidth="xl">
          <Button startIcon={<ArrowLeft size={18} />} onClick={() => navigate('/dashboard/service-providers')} sx={{ mb: 3 }}>
            Back to Service Providers
          </Button>
          <Card sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Provider ID #{id} was not found.
            </Typography>
          </Card>
        </Container>
      </Page>
    );
  }

  const currentBalanceNum = Number(formData.balance) || 0;
  const topUpNum = Number(formData.addBalance) || 0;
  const projectedBalance = currentBalanceNum + (topUpNum > 0 ? topUpNum : 0);

  let adexTokenSeverity = 'info';
  let adexTokenExpiryText =
    'Access token will be generated automatically on first checkout and saved for future requests.';

  if (provider.accessToken) {
    if (provider.tokenExpiresAt && new Date(provider.tokenExpiresAt) < new Date()) {
      adexTokenSeverity = 'warning';
    } else {
      adexTokenSeverity = 'success';
    }

    if (provider.tokenExpiresAt) {
      adexTokenExpiryText = `Token Expiration: ${new Date(provider.tokenExpiresAt).toLocaleString()} (Auto-renewed)`;
    } else {
      adexTokenExpiryText = 'Token Expiration: Never (Permanent token)';
    }
  }

  const renderVendorTriggerContent = () => {
    if (loadingTemplates) {
      return (
        <Box display="flex" alignItems="center" gap={1.5} py={0.5}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading vendor templates...
          </Typography>
        </Box>
      );
    }

    const currentDriver = formData.driverType || provider?.driverType || 'custom';
    const theme = getDriverTheme(currentDriver);
    const IconComp = theme.icon;
    const vendorDisplayName =
      selectedProcessor?.displayName ||
      (selectedProcessor ? selectedProcessor.name : 'Custom Gateway');

    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.75} sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: theme.bg,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconComp size={22} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', mb: 0.25 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                {vendorDisplayName}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                color: '#6B7280',
                fontFamily: 'monospace',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {formData.baseUrl || selectedProcessor?.baseUrl || 'Standard built-in driver endpoint'}
            </Typography>
          </Box>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          endIcon={<ChevronRight size={14} />}
          disabled={!canManage}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.785rem',
            borderRadius: '10px',
            borderColor: '#E5E7EB',
            color: '#374151',
            '&:hover': { borderColor: '#0066FE', color: '#0066FE', bgcolor: '#EFF6FF' },
          }}
        >
          Change
        </Button>
      </Stack>
    );
  };

  return (
    <Page title={`Edit Provider - ${provider.name} | Dashboard`}>
      <Container maxWidth="md">
        {/* Navigation Top Bar */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate(`/dashboard/service-providers/details/${provider.id}`)} sx={{ backgroundColor: '#f4f6f8' }}>
              <ArrowLeft size={20} />
            </IconButton>
            <div>
              <Typography variant="h4">Edit Service Provider Settings</Typography>
              <Typography variant="body2" color="text.secondary">
                Configure API keys, balance ledger tracking mode, and alert thresholds.
              </Typography>
            </div>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<History size={16} />}
              onClick={() => navigate(`/dashboard/audit-logs?search=${provider.slug || provider.name}`)}
              sx={{ textTransform: 'none' }}
            >
              Audit Trail
            </Button>
            <Button variant="outlined" color="inherit" onClick={() => navigate(`/dashboard/service-providers/details/${provider.id}`)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save size={18} />}
              onClick={handleSave}
              disabled={saving || !canManage}
              sx={{ textTransform: 'none' }}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Stack>
        </Stack>

        {!canManage && (
          <Alert severity="warning" icon={<ShieldAlert size={20} />} sx={{ mb: 3 }}>
            <strong>Read-only access:</strong> You do not have permissions to modify service providers (Requires <code>processors:manage</code> or <code>services:write</code>).
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Card 1: Provider Identity & Credentials */}
          <Card sx={{ p: 4 }}>
            <Stack direction="row" alignItems="center" spacing={2} mb={3}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'primary.lighter',
                  color: 'primary.main',
                }}
              >
                <Server size={28} />
              </Box>
              <div>
                <Typography variant="h6">{formData.name || provider.name}</Typography>
                <Stack direction="row" spacing={1} mt={0.5}>
                  <Chip label={`Slug: ${provider.slug}`} size="small" variant="outlined" />
                  <Chip
                    label={formData.isActive ? 'Active' : 'Disabled'}
                    size="small"
                    sx={{
                      bgcolor: formData.isActive ? '#ECFDF5' : '#F3F4F6',
                      color: formData.isActive ? '#047857' : '#6B7280',
                      fontWeight: 600,
                    }}
                  />
                </Stack>
              </div>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              {/* Vendor Template Selector Card */}
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Vendor Gateway Template
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    disabled={!canManage}
                    onClick={() => setOpenVendorModal(true)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      color: '#0066FE',
                      p: 0,
                      minWidth: 'auto',
                      '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                    }}
                  >
                    Change vendor template
                  </Button>
                </Box>

                <Card
                  onClick={() => canManage && setOpenVendorModal(true)}
                  sx={{
                    p: 2,
                    borderRadius: '14px',
                    bgcolor: '#FFFFFF',
                    border: '1.5px solid #E5E7EB',
                    cursor: canManage ? 'pointer' : 'default',
                    transition: 'all 0.15s ease-in-out',
                    '&:hover': canManage
                      ? {
                          borderColor: '#0066FE',
                          bgcolor: '#FAFCFF',
                          boxShadow: '0 4px 12px rgba(0, 102, 254, 0.05)',
                        }
                      : {},
                  }}
                >
                  {renderVendorTriggerContent()}
                </Card>
                <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mt: 0.75 }}>
                  Select or switch between QuicklySim, Datatap, SMEPlug, or custom configurations.
                </Typography>
              </Box>

              <TextField
                label="Provider Account Name"
                fullWidth
                disabled={!canManage}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                helperText="Descriptive label for this vendor account in the admin dashboard"
              />

              <TextField
                label="Unique Slug Identifier"
                fullWidth
                disabled
                value={provider.slug}
              />

              {isAdexDriver ? (
                <>
                  <TextField
                    label="Portal Username / Email"
                    fullWidth
                    disabled={!canManage}
                    value={adexUsername}
                    onChange={(e) => {
                      setAdexUsername(e.target.value);
                      if (keyValidation) setKeyValidation(null);
                    }}
                    helperText="Vendor portal username or email used for authentication"
                  />

                  <TextField
                    label="Portal Password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    disabled={!canManage}
                    value={adexPassword}
                    onChange={(e) => {
                      setAdexPassword(e.target.value);
                      if (keyValidation) setKeyValidation(null);
                    }}
                    helperText="Stored securely. Access tokens are generated and renewed automatically on checkout."
                    InputProps={{
                      endAdornment: (
                        <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </IconButton>
                      ),
                    }}
                  />

                  <Alert severity={adexTokenSeverity}>
                    <strong>Vendor Access Token:</strong>{' '}
                    {provider.accessToken
                      ? `${provider.accessToken.slice(0, 20)}...`
                      : 'None yet (will be generated automatically on first checkout)'}
                    <br />
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                      {adexTokenExpiryText}
                    </Typography>
                  </Alert>
                </>
              ) : (
                <>
                  <TextField
                    label="Account / Vendor Email"
                    fullWidth
                    disabled={!canManage}
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (keyValidation) setKeyValidation(null);
                    }}
                    helperText={
                      isAutopilotDriver
                        ? "Registered email on Autopilot platform (required for live wallet balance sync)"
                        : "Optional provider account email (used if required by vendor for authentication or balance sync)"
                    }
                    placeholder="e.g. user@vendor.com"
                  />

                  <TextField
                    label="API Key / Secret Token"
                    type={showApiKey ? 'text' : 'password'}
                    fullWidth
                    disabled={!canManage}
                    value={formData.apiKey}
                    onChange={(e) => {
                      setFormData({ ...formData, apiKey: e.target.value });
                      if (keyValidation) setKeyValidation(null);
                    }}
                    helperText="Stored securely and used for automated multi-processor failover"
                    InputProps={{
                      endAdornment: (
                        <IconButton size="small" onClick={() => setShowApiKey(!showApiKey)}>
                          {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                        </IconButton>
                      ),
                    }}
                  />
                </>
              )}

              <TextField
                label="Base Endpoint URL"
                fullWidth
                disabled
                placeholder="e.g. https://api.smeplug.ng"
                value={formData.baseUrl}
                helperText="Pre-configured by vendor template (editing disabled for safety)"
              />

              {/* Credential Verification Status & Action */}
              <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#F9FAFB', border: '1px solid #ECEEF1' }}>
                {renderVerificationStatus()}
              </Box>

              <TextField
                label="Low Balance Alert Threshold (₦)"
                type="number"
                fullWidth
                disabled={!canManage}
                value={formData.lowBalanceThreshold}
                onChange={(e) => setFormData({ ...formData, lowBalanceThreshold: Number(e.target.value) })}
                helperText="Alert badge turns yellow/warning when balance drops below this amount"
              />

              <Alert severity="info">
                <strong>Multi-Processor Protection:</strong> Disabling this provider account immediately prevents the V2 Engine from sending new orders to this vendor until re-enabled.
              </Alert>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    disabled={!canManage}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="subtitle1" fontWeight="bold">
                    {formData.isActive ? 'Active Vendor Account' : 'Disabled / Inactive Account'}
                  </Typography>
                }
              />
            </Stack>
          </Card>

          {/* Card 2: Balance Tracking & Ledger Management */}
          <Card sx={{ p: 4 }}>
            <Stack direction="row" alignItems="center" spacing={2} mb={3}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#EFF6FF',
                  color: '#0066FE',
                }}
              >
                <Wallet size={28} />
              </Box>
              <div>
                <Typography variant="h6">Balance & Ledger Configuration</Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure whether balance is synced via upstream API or tracked in a manual virtual ledger.
                </Typography>
              </div>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              {/* Balance Mode Selection */}
              <TextField
                select
                fullWidth
                label="Balance Tracking Mode"
                disabled={!canManage}
                value={formData.balanceMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  setFormData({
                    ...formData,
                    balanceMode: mode,
                    supportsBalanceCheck: mode === 'api_sync',
                  });
                }}
                helperText="Determines how the wallet balance is maintained and updated"
              >
                <MenuItem value="api_sync">
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Live API Synchronized (Default)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Queries provider's upstream check-balance endpoint periodically.
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="manual_ledger">
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Manual Virtual Ledger
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      For providers without a balance API. You set/top up balance; orders auto-decrement it on checkout.
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="unlimited">
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Unlimited Balance Mode
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Post-paid / unmetered vendor account. Bypasses balance threshold checks during checkout.
                    </Typography>
                  </Box>
                </MenuItem>
              </TextField>

              {/* Automated Balance Check Switch */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.supportsBalanceCheck}
                      disabled={!canManage || formData.balanceMode === 'unlimited'}
                      onChange={(e) => {
                        const { checked } = e.target;
                        setFormData({
                          ...formData,
                          supportsBalanceCheck: checked,
                          balanceMode: checked ? 'api_sync' : 'manual_ledger',
                        });
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <div>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Automated API Balance Polling
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.supportsBalanceCheck
                          ? 'Background sync job will periodically query the provider API to refresh balance.'
                          : 'Disabled: Cron sync will NOT overwrite this balance. Orders will auto-deduct from this balance.'}
                      </Typography>
                    </div>
                  }
                />
              </Box>

              {/* Current Balance & Top Up Grid */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Current Recorded Balance (₦)"
                    type="number"
                    fullWidth
                    disabled={!canManage}
                    value={formData.balance}
                    onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                    helperText="Set exact base balance (useful for calibration or manual ledger setup)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography fontWeight={700}>₦</Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Add to Balance / Top-Up (₦)"
                    type="number"
                    fullWidth
                    disabled={!canManage}
                    placeholder="e.g. 40000"
                    value={formData.addBalance}
                    onChange={(e) => setFormData({ ...formData, addBalance: e.target.value })}
                    helperText="e.g. 'I've added ₦40,000'. Increments current balance on save."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography fontWeight={700} color="primary.main">
                            +₦
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {/* Calculation Preview if top up entered */}
              {topUpNum > 0 && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#ECFDF5',
                    border: '1px solid #A7F3D0',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#065F46', fontWeight: 600 }}>
                    Projected New Balance:{' '}
                    <strong>
                      ₦{projectedBalance.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>{' '}
                    (Current ₦{currentBalanceNum.toLocaleString('en-NG')} + Top Up ₦{topUpNum.toLocaleString('en-NG')})
                  </Typography>
                </Box>
              )}

              {/* Audit Reason / Notes */}
              <TextField
                label="Audit Reason / Notes (Optional)"
                fullWidth
                disabled={!canManage}
                placeholder="e.g. Top up via bank transfer ref #928374 or balance reconciliation"
                value={formData.auditNotes}
                onChange={(e) => setFormData({ ...formData, auditNotes: e.target.value })}
                helperText="Recorded permanently in tenant audit logs for accountability"
              />
            </Stack>
          </Card>
        </Stack>

        <VendorSelectModal
          open={openVendorModal}
          onClose={() => setOpenVendorModal(false)}
          processors={processors}
          selectedProcessor={selectedProcessor}
          onSelectProcessor={handleSelectProcessor}
          loading={loadingTemplates}
        />
      </Container>
    </Page>
  );
}

